// frontend/src/contexts/AuthContext.jsx
// 認證狀態管理 Context

import { createContext, useState, useEffect, useCallback } from 'react';
import authService, { api } from '../services/authService';
import tokenManager from '../services/tokenManager';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 初始化：從 storage 恢復登入狀態
  useEffect(() => {
    const initAuth = () => {
      try {
        // 使用 tokenManager 檢查認證狀態
        const authenticated = tokenManager.isAuthenticated();
        const storedUser = localStorage.getItem('user');

        if (authenticated && storedUser) {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);

          // TokenManager 會自動設定自動刷新
          console.log('[AuthContext] 使用者已登入，自動刷新已啟動');
        }
      } catch (error) {
        console.error('初始化認證狀態錯誤:', error);
        // 如果資料損壞，清除所有資料
        tokenManager.clearTokens();
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // 訂閱 Token 更新事件
    const unsubscribe = tokenManager.subscribe((newToken) => {
      console.log('[AuthContext] Token 已更新');
      // Token 更新時，可能需要重新獲取使用者資訊
      // 這裡保持簡單，只記錄事件
    });

    // 清理訂閱
    return () => {
      unsubscribe();
    };
  }, []);

  // 登入函數
  const login = useCallback((tokenData, userData) => {
    // tokenData 包含 accessToken（refreshToken 由 HttpOnly Cookie 管理）
    if (tokenData.accessToken) {
      // 使用 tokenManager 處理登入
      // 注意：handleLogin 只接受 2 個參數（accessToken, expiresIn）
      // refreshToken 由後端透過 HttpOnly Cookie 自動設定
      tokenManager.handleLogin(tokenData.accessToken, tokenData.expiresIn || 900);
    }

    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(userData));

    // 設定 axios 預設 header（tokenManager 會自動處理）
    authService.setAuthHeader(tokenData.accessToken);
  }, []);

  // 登出函數
  const logout = useCallback(async () => {
    try {
      // 呼叫登出 API（可選）
      await authService.logout();
    } catch (error) {
      console.error('登出 API 錯誤:', error);
    } finally {
      // 使用 tokenManager 處理登出
      tokenManager.handleLogout();

      // 清除狀態
      setUser(null);
      setIsAuthenticated(false);

      // 清除使用者資訊
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');

      // 清除 axios header
      authService.clearAuthHeader();
    }
  }, []);

  // 更新使用者資訊
  const updateUser = useCallback((userData) => {
    setUser(userData);

    // 更新 storage 中的使用者資訊（使用與 tokenManager 一致的 key）
    const storage = localStorage.getItem('accessToken') ? localStorage : sessionStorage;
    storage.setItem('user', JSON.stringify(userData));
  }, []);

  // 從後端重新獲取使用者資訊
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/user/profile');
      if (response.data?.success) {
        const userData = response.data.data;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('刷新使用者資訊失敗:', error);
      // 如果是 401 或 403 錯誤，可能需要登出
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
    }
    return null;
  }, [logout]);

  // 檢查是否有特定角色
  const hasRole = useCallback(
    (requiredRole) => {
      if (!user || !user.role) return false;

      // 角色層級：admin > advanced > user；research 比照 user（可搜尋，不能上傳）
      const roleHierarchy = {
        user: 1,
        research: 1,
        advanced: 2,
        admin: 3,
      };

      const userLevel = roleHierarchy[user.role] || 0;
      const requiredLevel = roleHierarchy[requiredRole] || 0;

      return userLevel >= requiredLevel;
    },
    [user]
  );

  // 檢查是否為管理員
  const isAdmin = useCallback(() => {
    return user?.role === 'admin';
  }, [user]);

  // 檢查是否為進階使用者以上
  const isAdvanced = useCallback(() => {
    return user?.role === 'admin' || user?.role === 'advanced';
  }, [user]);

  // 從 tokenManager 取得當前 token
  const getToken = useCallback(() => {
    return tokenManager.getAccessToken();
  }, []);

  const contextValue = {
    isAuthenticated,
    user,
    token: getToken(), // 動態取得 token
    getToken, // 提供取得 token 的方法
    loading,
    login,
    logout,
    updateUser,
    refreshUser, // 從後端重新獲取使用者資訊
    hasRole,
    isAdmin,
    isAdvanced,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default AuthContext;
